// utils/formatCurrency.ts
//
// Formats a monetary amount with the correct sign for its ISO 4217 currency code.
// Examples (en-US): USD → "$1,234.50", EUR → "€99.00", JPY → "¥1,000", CAD → "CA$75.00".
//
// Design notes for anyone new to this file:
// - This is a PURE function (no React, no table, no globals besides a cache),
//   so it can be unit-tested directly and reused anywhere amounts are shown.
// - Symbols and decimal places come from the browser's built-in Intl API, which
//   is backed by CLDR (Unicode Common Locale Data Repository), the same data
//   iOS, Android, Java and most payment dashboards use. We never hardcode "$".

/** Shown when the amount is missing or not a valid number. */
const MISSING_AMOUNT_PLACEHOLDER = '—';

/** ISO 4217 currency codes are exactly three uppercase letters, e.g. USD, EUR, JPY. */
const ISO_4217_CODE_PATTERN = /^[A-Z]{3}$/;

/** Fraction digits used when there is no currency to infer precision from. */
const DEFAULT_FRACTION_DIGITS = 2;

/** Locale used when the caller does not pass one. */
// TODO: Confirm with product whether this should follow the user's UI language
//       (i18n.language) instead. Changing it only requires passing `locale` in.
export const DEFAULT_LOCALE = 'en-US';

type AmountInput = string | number | null | undefined;
type CurrencyCodeInput = string | null | undefined;

// WHAT: Caches one Intl.NumberFormat per locale+currency pair.
// WHY: Constructing a formatter is far more expensive than calling .format(),
//      and table cells re-render on every sort, filter and page change.
// GOTCHA: The cache is module-level and lives for the whole session. That is fine
//         because the key space is tiny (a few locales × a few currencies).
const formatterCache = new Map<string, Intl.NumberFormat>();

function getCurrencyFormatter(locale: string, currencyCode: string): Intl.NumberFormat {
  const cacheKey = `${locale}|${currencyCode}`;
  const cachedFormatter = formatterCache.get(cacheKey);
  if (cachedFormatter) return cachedFormatter;

  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    // Explicit on purpose: CLDR's 'symbol' shows "$" for USD but "CA$" / "A$" /
    // "MX$" for other dollar currencies, so a payout can't be misread as USD.
    // 'narrowSymbol' would show "$" for all of them. Don't switch this without
    // a product decision.
    currencyDisplay: 'symbol',
  });
  formatterCache.set(cacheKey, formatter);
  return formatter;
}

// WHAT: Converts API input into a finite number, or null if it isn't one.
// WHY: Number() is too forgiving to trust directly:
//      - Number('') and Number('  ') are 0, which would show "$0.00" for MISSING data.
//      - Number('abc') is NaN, which Intl happily renders as "$NaN" (it does not throw).
function toFiniteNumber(value: AmountInput): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;

  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// WHAT: Formats a plain number with no currency sign.
// WHY: Used when the currency is missing or malformed. We deliberately do NOT
//      default to USD, because showing "$" on a EUR payout would be actively wrong.
function formatPlainAmount(amount: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: DEFAULT_FRACTION_DIGITS,
    maximumFractionDigits: DEFAULT_FRACTION_DIGITS,
  }).format(amount);
}

/**
 * Formats an amount with the sign for its ISO 4217 currency code.
 *
 * @param amount       Number or numeric string from the API (e.g. "1234.5").
 * @param currencyCode ISO 4217 code (e.g. "USD"). Case and surrounding whitespace are ignored.
 * @param locale       BCP 47 locale controlling separators and symbol placement.
 *
 * @example formatCurrency('1234.5', 'USD') // "$1,234.50"
 * @example formatCurrency(1000, 'JPY')     // "¥1,000"   (0 decimals for yen)
 * @example formatCurrency(10, null)        // "10.00"    (no guessed symbol)
 * @example formatCurrency('abc', 'USD')    // "—"
 */
export function formatCurrency(
  amount: AmountInput,
  currencyCode: CurrencyCodeInput,
  locale: string = DEFAULT_LOCALE,
): string {
  const numericAmount = toFiniteNumber(amount);
  if (numericAmount === null) return MISSING_AMOUNT_PLACEHOLDER;

  // Normalise first: the API may send "usd" or " USD ", and our validation
  // regex expects exactly three uppercase letters.
  const normalizedCode = currencyCode?.trim().toUpperCase() ?? '';

  // A missing or malformed currency is an EXPECTED data state, so we check for it
  // explicitly instead of letting Intl throw and relying on catch as control flow.
  if (!ISO_4217_CODE_PATTERN.test(normalizedCode)) {
    return formatPlainAmount(numericAmount, locale);
  }

  try {
    return getCurrencyFormatter(locale, normalizedCode).format(numericAmount);
  } catch (error) {
    // Defensive only: after the checks above this is reached mainly when the
    // locale is invalid or unsupported by an older browser. Never swallow silently.
    // TODO: Replace console.warn with the app's logger / monitoring (e.g. Sentry).
    console.warn('formatCurrency failed', { amount, currencyCode, locale, error });
    return `${normalizedCode} ${numericAmount.toFixed(DEFAULT_FRACTION_DIGITS)}`;
  }
}
